import {
  UnitOfTimeEnum,
  Notification,
  defaultMaintenanceMsg,
  NotificationType,
  NotificationDetails,
  defaultDateFormatToBeUsed,
} from '@ssar-app/domain';
import moment from 'moment';
import { ProgressStep } from '@ssar-app/uikit';
import { format as stringFormat } from 'react-string-format';

export interface NotificationDetailsClientModel {
  startTime: number;
  endTime: number;
  notifyOn: number;
  timeZone: string;
  duration: number;
  unitOfTime: number;
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export const currentTzMillis = (timeZone: string) =>
  moment().tz(timeZone).valueOf();

export const selectedTzMillis = (time: number, timeZone: string) =>
  moment(time).tz(timeZone).valueOf();

export const isSameDateNotification = (notification: Notification) => {
  const sTime = moment(notification.details.startTime).tz(
    notification.details.timeZone,
  );
  const eTime = moment(notification.details.endTime).tz(
    notification.details.timeZone,
  );
  return sTime.date() === eTime.date();
};

export const getNotifyTime = (item: NotificationDetails) => {
  const newTime = moment(item.startTime).tz(item.timeZone);
  const duration = item.duration;
  const unitOfTime = item.unitOfTime;
  if (duration === -1) {
    return newTime.valueOf();
  }
  newTime.set({
    date: newTime.date() - (unitOfTime === UnitOfTimeEnum.DAYS ? duration : 0),
    hour: newTime.hour() - (unitOfTime === UnitOfTimeEnum.HOURS ? duration : 0),
  });
  return newTime.valueOf();
};

export const getUpdatedStepData = (
  stepOptions: ProgressStep[],
  step: number,
  isErrors: boolean,
) => {
  if (isErrors) {
    const updatedData = stepOptions.map((items) => {
      return items.id === step
        ? { ...items, errorMessage: 'Missing required inputs' }
        : items;
    });
    return updatedData;
  } else {
    const updatedData = stepOptions.map((items) => {
      return items.id === step ? { ...items, errorMessage: '' } : items;
    });
    return updatedData;
  }
};

const getTimeZoneShortName = (timeZone: string) =>
  moment().tz(timeZone).format('z');

export const convertToTz = (
  time: number | string | undefined,
  timeZone: string,
) => moment(time).tz(timeZone, true);

export const convertToMillis = (time: number | string | Date | undefined) =>
  moment(time).valueOf();

const getNotificationTimeString = (
  notification: Notification,
  sameDate: boolean,
) => {
  const timeZone = notification.details.timeZone;
  const sTime = millisToDateTime(
    notification.details.startTime,
    notification.details.timeZone,
  );
  const eTime = millisToDateTime(
    notification.details.endTime,
    notification.details.timeZone,
  );
  return sameDate
    ? `${sTime?.format('DD/MM/YYYY')} from ${sTime?.format(
        'hh:mm A',
      )} ${getTimeZoneShortName(timeZone)} until ${eTime?.format(
        'hh:mm A',
      )} ${getTimeZoneShortName(timeZone)}`
    : `${moment(notification.details.startTime)
        .tz(timeZone)
        .format('DD/MM/YYYY hh:mm A')} ${getTimeZoneShortName(
        timeZone,
      )}  to ${moment(notification.details.endTime)
        .tz(timeZone)
        .format('DD/MM/YYYY hh:mm A')} ${getTimeZoneShortName(timeZone)}`;
};

export const getDescription = (notification: Notification) => {
  if (notification.type != NotificationType.MAINTENANCE) {
    return notification.description;
  }
  if (!notification.details.startTime || !notification.details.endTime) {
    return notification.description;
  }
  const sameDate = isSameDateNotification(notification);
  const notifyTimeString = getNotificationTimeString(notification, sameDate);
  return stringFormat(
    defaultMaintenanceMsg,
    sameDate ? `on ${notifyTimeString}` : `from ${notifyTimeString}`,
  );
};

export const getAnnouncementTime = (notification: Notification) => {
  const sameDate = isSameDateNotification(notification);
  return getNotificationTimeString(notification, sameDate);
};

export const getPublishTime = (notification: Notification) => {
  let notifTime = currentTzMillis(notification.details.timeZone);
  if (notification.details.duration !== 0)
    notifTime = getNotifyTime(notification.details);
  const nTime = millisToDateTime(notifTime, notification.details.timeZone);
  return `${nTime?.format('DD/MM/YYYY')} at ${nTime?.format('hh:mm A')} ${moment
    .tz(notification.details.timeZone)
    .format('z')}`;
};

export const millisToDateTime = (time: number | undefined, timeZone: string) =>
  time ? moment(time).tz(timeZone) : undefined;

export const formatMillisToDateTime = (
  time: number,
  timeZone: string,
  format?: string,
) =>
  time
    ? moment(time)
        .tz(timeZone)
        .format(format ? format : defaultDateFormatToBeUsed)
    : undefined;
